/**
 * Promoted Blueprint Adapter
 * Transforms promoted blueprint data between API and application layer
 */

import { BaseAdapter } from './base.adapter';
import type {
  KnowledgeBlueprint,
  WorkflowBlueprint,
  KnowledgeBlueprintResponseDto,
  WorkflowBlueprintResponseDto
} from '@/modules/promoted-blueprints/types';

export class PromotedBlueprintAdapter extends BaseAdapter {
  /**
   * Adapts a knowledge blueprint from API response
   */
  static fromKnowledgeApi(dto: KnowledgeBlueprintResponseDto): KnowledgeBlueprint {
    return {
      id: dto._id,
      unitId: dto.unit_id,
      intent: dto.intent,
      question: dto.question,
      state: dto.state || '',
      enabled: dto.enabled ?? true,
      autoSend: dto.autoSend ?? true,
      template: dto.blueprint?.templates?.[0] ?? '',
      templates: dto.blueprint?.templates ?? [],
      responsePolicy: dto.blueprint?.response_policy
    };
  }

  /**
   * Adapts knowledge blueprints array from API response
   */
  static fromKnowledgeApiList(dtos: KnowledgeBlueprintResponseDto[]): KnowledgeBlueprint[] {
    return dtos.map((dto) => this.fromKnowledgeApi(dto));
  }

  /**
   * Adapts a workflow blueprint from API response
   */
  static fromWorkflowApi(dto: WorkflowBlueprintResponseDto): WorkflowBlueprint {
    return {
      id: dto._id,
      intent: dto.intent,
      state: dto.state || '',
      enabled: dto.enabled ?? false,
      templatesEnabled: dto.templatesEnabled || {},
      templateNames: dto.templateNames || {},
      templateDetails: dto.templateDetails || {},
      templates: dto.blueprint?.templates || [],
      responsePolicy: dto.blueprint?.response_policy
    };
  }

  /**
   * Adapts workflow blueprints array from API response
   */
  static fromWorkflowApiList(dtos: WorkflowBlueprintResponseDto[]): WorkflowBlueprint[] {
    return dtos.map((dto) => this.fromWorkflowApi(dto));
  }
}
